import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import type { BulkActionInValidStatusResponse } from "@/types/draftCalendar";

interface BulkPushToSummitResultDialogProps {
  open: boolean;
  onClose: () => void;
  totalCount: number;
  successCount: number;
  failedMeetings: BulkActionInValidStatusResponse[];
}

export function BulkPushToSummitResultDialog({
  open,
  onClose,
  totalCount,
  successCount,
  failedMeetings
}: BulkPushToSummitResultDialogProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
      return;
    }

    previousFocusRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const hasFailures = failedMeetings.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      tabIndex={-1}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-push-result-title"
        className="w-full max-w-3xl rounded border border-slate-300 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="bulk-push-result-title" className="text-base font-semibold text-slate-900">
            {hasFailures ? "Push Completed with Errors" : "Success: All Meetings Pushed to Summit"}
          </h2>
          <button type="button" aria-label="Close" onClick={onClose} ref={closeButtonRef} className="text-slate-500 hover:text-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-4 py-4">
          <p className="text-sm text-slate-700">
            <strong>{successCount}</strong> of <strong>{totalCount}</strong> meetings were successfully pushed to Summit.
          </p>

          {hasFailures && (
            <div>
              <p className="mb-2 text-sm font-medium text-slate-800">Meetings not pushed to Summit:</p>
              <div className="max-h-72 overflow-auto rounded border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                    <tr>
                      <th className="px-3 py-2">ID</th>
                      <th className="px-3 py-2">Prior ID</th>
                      <th className="px-3 py-2">Meeting Name</th>
                      <th className="px-3 py-2">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failedMeetings.map((m) => (
                      <tr key={m.id} className="border-t border-slate-200">
                        <td className="px-3 py-2">{m.id}</td>
                        <td className="px-3 py-2">{m.priorId || "-"}</td>
                        <td className="px-3 py-2">{m.meetingName || "-"}</td>
                        <td className="px-3 py-2">{m.errorMessage || "Unknown error"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
